import { useState } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import type {
  WorkflowVersionSummary,
  PinVersionResponse,
  StaleBaseWarning,
} from '@erp/rpc';
import { useGT } from 'gt-react';
import { useToast } from '@/context/ToastContext';
import {
  usePinVersion,
  useDeleteDraft,
} from '@/api/rpc/hooks/workflows/useWorkflowVersioning';

export function useVersionPin(
  version: WorkflowVersionSummary,
  onPinChanged?: (id: string) => void,
) {
  const router = useRouter();
  const { showToast } = useToast();
  const gt = useGT();
  const pinMutation = usePinVersion();
  const [pinStaleWarning, setPinStaleWarning] =
    useState<StaleBaseWarning | null>(null);
  const handlePin = async (confirmWorkflowName?: string) => {
    try {
      const res: PinVersionResponse = await pinMutation.mutateAsync({
        workflowDefinitionId: version.id,
        acknowledgeStaleBase: confirmWorkflowName !== undefined,
        confirmWorkflowName,
      });
      if (!res.ok) {
        setPinStaleWarning(res.warning);
        return;
      }
      setPinStaleWarning(null);
      showToast({
        title: gt('Published v{version}', { version: res.definition.version }),
        message: gt('New triggers will use this version.'),
        color: 'success',
      });
      // Layout-route loader still holds the stale `definition.isPinned: false`
      // — invalidate so the picker trigger and status card pick up the publish.
      await router.invalidate();
      onPinChanged?.(res.definition.id);
    } catch (err) {
      showToast({
        title: gt('Publish failed'),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
    }
  };

  return {
    pinMutation,
    pinStaleWarning,
    closeWarning: () => setPinStaleWarning(null),
    handlePin,
  };
}

export function useVersionDraftDelete(
  version: WorkflowVersionSummary,
  options: { isViewing: boolean; onView?: () => void },
) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const gt = useGT();
  const deleteDraftMutation = useDeleteDraft();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteDraftMutation.mutateAsync({
        workflowDefinitionId: version.id,
      });
      setDeleteConfirmOpen(false);
      if (res.workflowDeleted) {
        // The draft was the only version, so the whole workflow went with it —
        // there is no overview left to pop back to.
        showToast({
          title: gt('Workflow deleted'),
          message: '',
          color: 'success',
        });
        options.onView?.(); // closes the panel — see handleView
        void navigate({ to: '/workflows' });
        return;
      }
      showToast({
        title: gt('Deleted draft v{version}', { version: version.version }),
        message: '',
        color: 'success',
      });
      // If the user was viewing the deleted draft, pop them back to the
      // workflow (resolves to the pinned version via the route loader).
      if (options.isViewing) {
        void navigate({
          to: '/workflows/$workflowId',
          params: { workflowId: version.workflowId },
        });
      }
    } catch (err) {
      showToast({
        title: gt('Delete failed'),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
    }
  };

  return {
    deleteDraftMutation,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    handleConfirmDelete,
  };
}
